import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Brand } from "../Brand";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, AlertOctagon, Loader2 } from "lucide-react";


const ROLE_LABEL = {
  admin:      "Admin",
  treasury:   "Treasury",
  compliance: "Compliance",
  support:    "Support",
};

const ROLE_DESCRIPTION = {
  admin:      "Full back-office access including team management.",
  treasury:   "Approve fund movements, refunds, wallet whitelists.",
  compliance: "Review KYC, Source-of-Funds, and risk flags.",
  support:    "Customer 360 read-only — no fund actions.",
};


export default function StaffInviteRedeem() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/team/invite/${token}`)
      .then((r) => { if (alive) { setInvite(r.data); setName(r.data?.name || ""); } })
      .catch((e) => {
        if (!alive) return;
        const code = e?.response?.status;
        const detail = e?.response?.data?.detail || "Could not load this invitation.";
        setError({ code, detail });
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const submit = async () => {
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/team/invite/${token}/redeem`, {
        token, password, name: name.trim() || undefined,
      });
      toast.success("Account created. Please log in to enable 2FA.");
      // 🔒 force them through the standard login flow — they MUST enrol 2FA
      // on first sign-in (must_enroll_2fa flag will gate them at /admin).
      setTimeout(() => navigate("/login"), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem invitation.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Brand size={32} />
        </div>

        {loading && (
          <div className="xch-card p-8 text-center" data-testid="invite-loading">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" />
            <div className="mt-3 text-sm text-muted-foreground">Loading invitation…</div>
          </div>
        )}

        {!loading && error && (
          <div className="xch-card p-6" data-testid="invite-error">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-rose-50 border border-rose-200 shrink-0">
                <AlertOctagon className="w-5 h-5 text-rose-700" />
              </div>
              <div>
                <div className="font-display font-bold text-foreground">
                  {error.code === 410 ? "This invitation is no longer valid" : "Invitation not found"}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">{error.detail}</p>
                <p className="text-xs text-muted-foreground mt-3">
                  Ask the admin who invited you to send a fresh invitation.
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && invite && (
          <div className="xch-card p-6" data-testid="invite-form-card">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-indigo-50 border border-indigo-200 shrink-0">
                <ShieldCheck className="w-5 h-5 text-indigo-700" />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-foreground">You&apos;re invited to the Xchangely team</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Invited by <strong>{invite.invited_by_email || "an admin"}</strong>
                </p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] uppercase tracking-wider font-bold"
                         data-testid="invite-role-badge">
                    {ROLE_LABEL[invite.role] || invite.role}
                  </Badge>
                  <span className="text-[11px] text-muted-foreground">{invite.email}</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                  {ROLE_DESCRIPTION[invite.role] || "Staff role"}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="invite-name" className="text-xs">Your name</Label>
                <Input id="invite-name" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Alex Compliance"
                       data-testid="invite-name-input" />
              </div>
              <div>
                <Label htmlFor="invite-password" className="text-xs">Set a password (min 8 characters)</Label>
                <Input id="invite-password" type="password" value={password}
                       onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                       data-testid="invite-password-input" />
              </div>
              <div>
                <Label htmlFor="invite-password2" className="text-xs">Confirm password</Label>
                <Input id="invite-password2" type="password" value={password2}
                       onChange={(e) => setPassword2(e.target.value)} autoComplete="new-password"
                       data-testid="invite-password2-input" />
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2"
                 data-testid="invite-2fa-notice">
              <KeyRound className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-[11px] text-amber-900 leading-relaxed">
                <strong>2-factor authentication is mandatory.</strong> After you click &quot;Create my account&quot;,
                you&apos;ll be sent to the login page — on first sign-in you&apos;ll be walked through enabling
                an authenticator app (Google Authenticator, 1Password, etc.). You won&apos;t be able to reach
                the back-office until 2FA is set up.
              </div>
            </div>

            <Button onClick={submit} disabled={busy || !password || password !== password2}
                    className="w-full mt-5 rounded-xl gap-1.5 bg-primary hover:bg-primary/90 text-white"
                    data-testid="invite-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {busy ? "Creating account…" : "Create my account"}
            </Button>

            <p className="text-[10px] text-muted-foreground text-center mt-3">
              By creating this account you agree to operate within Xchangely&apos;s compliance policies and audit trail.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
